import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchApi } from '../lib/api';
import type { PayrollMonthView, PayrollAdjustmentLine, TeacherPaySettingDto } from '@repo/shared-types';
import type { UpsertPaySettingInput, CreateAdjustmentInput } from '@repo/validation';
import i18n from '../i18n';

// Every payroll query/invalidation shares this prefix so a mutation on one
// month (or one teacher) safely refreshes any other month/teacher view that
// might be mounted (e.g. Task 6's month picker).
const PAYROLL_KEY = ['payroll'] as const;

export function usePayrollMonth(month: string, branchId?: string) {
  return useQuery<PayrollMonthView>({
    queryKey: [...PAYROLL_KEY, month, branchId ?? null],
    queryFn: () => fetchApi(`/payroll?month=${month}${branchId ? `&branchId=${branchId}` : ''}`),
  });
}

export function useUpsertPaySetting(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertPaySettingInput) =>
      fetchApi<TeacherPaySettingDto>(`/org/members/${membershipId}/pay-setting`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      // The rate dialog reads its prefill from GET /org/members (see TeamTab),
      // and any mounted payroll month view resolves rates from the same source.
      qc.invalidateQueries({ queryKey: ['org', 'members'] });
      qc.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast.success(i18n.t('settings.saved', { ns: 'payroll' }));
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể lưu tiền công'),
  });
}

export function useAddAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      membershipId,
      month,
      data,
    }: {
      membershipId: string;
      month: string;
      data: CreateAdjustmentInput;
    }) =>
      fetchApi<PayrollAdjustmentLine>(`/payroll/periods/${membershipId}/${month}/adjustments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast.success('Đã thêm khoản điều chỉnh');
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể thêm khoản điều chỉnh'),
  });
}

export function useDeleteAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ deleted: boolean }>(`/payroll/adjustments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast.success('Đã xoá khoản điều chỉnh');
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể xoá khoản điều chỉnh'),
  });
}

export function useFinalizePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, month }: { membershipId: string; month: string }) =>
      fetchApi(`/payroll/periods/${membershipId}/${month}/finalize`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast.success('Đã chốt lương');
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể chốt lương'),
  });
}

export function useReopenPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, month }: { membershipId: string; month: string }) =>
      fetchApi(`/payroll/periods/${membershipId}/${month}/reopen`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast.success('Đã mở lại tháng lương');
    },
    onError: (e: Error) => toast.error(e.message || 'Không thể mở lại tháng lương'),
  });
}
